import { useCoachStyles } from "@/lib/queries";
import { useSessionContext } from "@/lib/session";
import type { CoachStyle } from "@nl/shared";

/**
 * The coach the signed-in advisor picked during onboarding.
 *
 * Returns null while the list is still loading or for roles that never pick
 * one, so callers can fall back to neutral wording instead of showing a
 * half-filled coach.
 */
export function useCoach(): CoachStyle | null {
  const { user } = useSessionContext();
  return useCoachById(user?.coachStyleId);
}

/**
 * The coach belonging to a specific session or run.
 *
 * Deliberately not `useCoach()`: an advisor who switched coaches last week
 * should still see who actually wrote an older piece of feedback.
 */
export function useCoachById(coachStyleId?: string | null): CoachStyle | null {
  const { data: coaches = [] } = useCoachStyles();
  if (!coachStyleId) return null;
  return coaches.find((coach) => coach.id === coachStyleId) ?? null;
}

/** The conversation's own coach, or the advisor's pick where it has none. */
export function useCoachFor(coachStyleId?: string | null): CoachStyle | null {
  const own = useCoach();
  const specific = useCoachById(coachStyleId);
  return coachStyleId ? specific : own;
}

/**
 * Says what calling the coach is, which is what tells it apart from the
 * dictation button sitting next to it in the same input bar.
 */
export function coachCallHint(coachName: string): string {
  return `Live-Gespräch: Du sprichst in Echtzeit mit ${coachName}, wie am Telefon. Das Transkript landet danach im Chat.`;
}

/**
 * German possessive for a coach name: "Lauras", but "Lukas'" — names ending in
 * an s-sound take a bare apostrophe, and "Lukass Einschätzung" would be wrong.
 */
export function possessive(name: string): string {
  return /[sßxz]$/i.test(name) ? `${name}'` : `${name}s`;
}

/**
 * Possessive form, but only for coaches that are actually a person.
 *
 * Runs from before the named coaches point at the retired styles, whose name
 * is a description rather than a name — "Coachend & erklärends Bewertung" is
 * nonsense. Those keep the neutral wording. The headline is the marker: only
 * the person-shaped coaches have one.
 */
export function coachPossessive(coach: CoachStyle | null): string | undefined {
  if (!coach?.headline) return undefined;
  return possessive(coach.name);
}
